<template>
  <div v-if="visible" class="sample-banner list-view-mx" data-e2e="sample-banner">
    <span class="sample-banner__text">
      {{ count === 1 ? 'This record is a sample' : `These ${count} records are samples` }}, added so you can see the
      workflow in use. Clear them when you have real ones — edited samples are cleared too.
    </span>
    <button type="button" class="sample-banner__clear" data-e2e="clear-samples" :disabled="clearing" @click="clear">
      {{ clearing ? 'Clearing…' : 'Clear samples' }}
    </button>
  </div>
</template>

<script>
/*
 * Guided onboarding P2 (docs/frontend-guided-onboarding-proposal.md). The first workflow opens with
 * sample records; this says so, on that module's list only, until they are cleared.
 *
 * It asks the server rather than guessing: the workspace's onboarding record holds the module the
 * starter built and the ids of the entries it seeded, and that list is the marker. When it is empty
 * the banner is gone for good. Any failure to read it just means no banner — a notice about sample
 * data must never be the reason a list does not load.
 */
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';

export default {
  name: 'FTSSampleRecordsBanner',
  props: {
    moduleId: { type: [Number, String], default: null },
  },
  data() {
    return {
      count: 0,
      entryIds: [],
      clearing: false,
    };
  },
  computed: {
    visible() {
      return this.count > 0;
    },
  },
  watch: {
    moduleId() {
      this.refresh();
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    async refresh() {
      this.count = 0;
      this.entryIds = [];
      if (!authorizationStore.getters.appGuidedFirstWorkflow || this.moduleId == null) return;
      try {
        const state = await clientStore.dispatch('getOnboarding');
        const workflow = state && state.workspace && state.workspace.firstWorkflow;
        if (!workflow || String(workflow.moduleId) !== String(this.moduleId)) return;
        this.entryIds = (workflow.sampleEntryIds || []).slice();
        this.count = this.entryIds.length;
      } catch (err) {
        this.count = 0;
        this.entryIds = [];
      }
    },
    async clear() {
      if (this.clearing) return;
      const samples = (this.entryIds || []).slice();
      try {
        this.clearing = true;
        const result = await clientStore.dispatch('clearOnboardingSamples');
        this.count = (result && result.remaining) || 0;
        // SS06130: the list refresh below only re-reads the rows. A sample open in the detail
        // pane would stay on screen, deleted, with its ?id left for a reload to fail on - so say
        // which entries are gone and the pane closes one it is showing.
        const removed = await this.clearedOf(samples);
        if (removed.length) {
          window.eventBus.$emit('entries:removed', { moduleId: this.moduleId, entryIds: removed });
        }
        window.eventBus.$emit('listing:refresh');
        if (this.count > 0) {
          this.$toasted.error(`${this.count} sample record${this.count === 1 ? '' : 's'} could not be cleared. Try again.`);
        }
      } catch (err) {
        this.$toasted.global.error(err);
      } finally {
        this.clearing = false;
      }
    },
    // The samples that are really gone: all of them, unless the server kept some back - then the
    // onboarding record says which remain.
    async clearedOf(samples) {
      if (this.count === 0) {
        this.entryIds = [];
        return samples;
      }
      try {
        const state = await clientStore.dispatch('getOnboarding');
        const workflow = state && state.workspace && state.workspace.firstWorkflow;
        this.entryIds = ((workflow && workflow.sampleEntryIds) || []).slice();
        const remaining = this.entryIds.map(String);
        return samples.filter((id) => !remaining.includes(String(id)));
      } catch (err) {
        return [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Amber notice, the system's informational tone: it is about the data, not an error. */
.sample-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 10px 14px;
  border: 1px solid #f2d99a;
  border-radius: var(--inistate-rounded-4, 4px);
  background: #fff8e6;
  font-size: 13px;
  line-height: 1.45;
  color: #6b4a00;
}

.sample-banner__text {
  flex: 1;
}

.sample-banner__clear {
  flex: none;
  padding: 4px 10px;
  border: 1px solid #e0c27a;
  border-radius: var(--inistate-rounded-4, 4px);
  background: #fff;
  font-size: 13px;
  font-weight: var(--inistate-font-weight-medium, 500);
  color: #8a5a00;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}
</style>
